export const ESRI_IMAGERY_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const ESRI_IDENTIFY_URL =
  'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/identify';

export const NATIONAL_ORTHO_WMS_URL = 'https://geodata.gov.md/geoserver/orthophoto/wms';

export type OrthoLayer = { layer: string; year: number };

// Surveys older than this are skipped: Esri underneath is newer everywhere.
const ORTHO_MIN_YEAR = 2020;

/** Used until the server's layer list arrives, or when it cannot be read. */
export const FALLBACK_ORTHO_LAYERS: OrthoLayer[] = [
  { layer: 'orthophoto:2021_ortofoto_moldova_sud', year: 2021 },
  { layer: 'orthophoto:2020_ortofoto_moldova_centru', year: 2020 },
];

/**
 * Pure: the recent surveys a GeoServer capabilities document lists, newest
 * first. AGCC titles every survey with its year ("2021, ortofoto Moldova,
 * sud"), so a new national flight shows up here without a code change.
 */
export function parseOrthoCapabilities(xml: string): OrthoLayer[] {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const out: OrthoLayer[] = [];
  for (const el of Array.from(doc.getElementsByTagName('Layer'))) {
    const name = directChildText(el, 'Name');
    if (!name) continue;
    const year = surveyYear(directChildText(el, 'Title') ?? '') ?? surveyYear(name);
    if (year !== null && year >= ORTHO_MIN_YEAR) out.push({ layer: name, year });
  }
  return out.sort((a, b) => b.year - a.year || a.layer.localeCompare(b.layer));
}

function directChildText(el: Element, tag: string): string | null {
  for (const child of Array.from(el.children)) {
    if (child.localName === tag) return child.textContent?.trim() || null;
  }
  return null;
}

function surveyYear(text: string): number | null {
  const m = /(?:^|\D)((?:19|20)\d{2})(?!\d)/.exec(text);
  return m ? Number(m[1]) : null;
}

export async function fetchNationalOrthoLayers(signal?: AbortSignal): Promise<OrthoLayer[]> {
  const params = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetCapabilities',
  });
  const res = await fetch(`${NATIONAL_ORTHO_WMS_URL}?${params}`, { signal });
  if (!res.ok) throw new Error(`orthophoto capabilities: HTTP ${res.status}`);
  const layers = parseOrthoCapabilities(await res.text());
  if (layers.length === 0) throw new Error('orthophoto capabilities: no recent survey');
  return layers;
}

/** WMS draws later layers on top, so the newest survey goes last. */
export function orthoWmsLayers(layers: readonly OrthoLayer[]): string {
  return [...layers]
    .reverse()
    .map((l) => l.layer)
    .join(',');
}

/** `2020-2021`, or a single year when every survey shares it. */
export function orthoYearRange(layers: readonly OrthoLayer[]): string {
  const years = layers.map((l) => l.year);
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? String(max) : `${min}-${max}`;
}

// Esri only publishes capture dates for its high-resolution imagery, which
// starts at this zoom; below it the tiles come from a 15 m global mosaic.
export const ESRI_DATED_MIN_ZOOM = 12;

export type ImageryInfo =
  | {
      kind: 'dated';
      source: 'esri' | 'ortho';
      /** ISO `YYYY-MM-DD`, or just `YYYY` when only the year is known. */
      date: string;
      provider?: string;
      resolutionM?: number;
    }
  | { kind: 'zoomIn' }
  | { kind: 'unknown' };

type EsriIdentifyResult = {
  layerId?: number;
  attributes?: Record<string, string | undefined>;
};

/** Pure: picks the capture date of the imagery Esri draws at `zoom`. */
export function parseEsriIdentify(
  body: { results?: EsriIdentifyResult[] },
  zoom: number,
): ImageryInfo {
  const shown = (body.results ?? [])
    .filter((r) => r.layerId === 0 && r.attributes)
    .map((r) => r.attributes!)
    .filter((a) => {
      const min = Number(a.MinMapLevel);
      const max = Number(a.MaxMapLevel);
      return (Number.isNaN(min) || zoom >= min) && (Number.isNaN(max) || zoom <= max);
    })
    .sort((a, b) => Number(b.DrawOrder ?? 0) - Number(a.DrawOrder ?? 0));

  for (const a of shown) {
    const date = esriDate(a);
    if (!date) continue;
    const res = Number(a['RESOLUTION (M)']);
    return {
      kind: 'dated',
      source: 'esri',
      date,
      provider: a.SOURCE || undefined,
      resolutionM: Number.isFinite(res) ? res : undefined,
    };
  }
  return { kind: 'unknown' };
}

function esriDate(a: Record<string, string | undefined>): string | null {
  const compact = a['DATE (YYYYMMDD)'];
  if (compact && /^\d{8}$/.test(compact)) {
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
  }
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(a.SRC_DATE2 ?? '');
  if (m) return `${m[3]}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`;
  return null;
}

export async function fetchEsriImageryInfo(
  lat: number,
  lon: number,
  zoom: number,
  signal?: AbortSignal,
): Promise<ImageryInfo> {
  if (zoom < ESRI_DATED_MIN_ZOOM) return { kind: 'zoomIn' };
  const params = new URLSearchParams({
    geometry: `${lon},${lat}`,
    geometryType: 'esriGeometryPoint',
    sr: '4326',
    layers: 'all:0',
    tolerance: '0',
    mapExtent: `${lon - 0.01},${lat - 0.01},${lon + 0.01},${lat + 0.01}`,
    imageDisplay: '256,256,96',
    returnGeometry: 'false',
    f: 'json',
  });
  const res = await fetch(`${ESRI_IDENTIFY_URL}?${params}`, { signal });
  if (!res.ok) return { kind: 'unknown' };
  return parseEsriIdentify(await res.json(), zoom);
}

/** Pure: a GeoServer raster answers a covered pixel with one opaque feature. */
export function orthoCovers(body: {
  features?: { properties?: Record<string, unknown> | null }[];
}): boolean {
  return (body.features ?? []).some((f) => {
    const alpha = f.properties?.ALPHA_BAND;
    return alpha === undefined || Number(alpha) > 0;
  });
}

/** Year of the newest survey covering the point, or null outside them all. */
export async function fetchNationalOrthoYear(
  lat: number,
  lon: number,
  layers: readonly OrthoLayer[],
  signal?: AbortSignal,
): Promise<number | null> {
  const d = 0.0005;
  for (const { layer, year } of layers) {
    const params = new URLSearchParams({
      service: 'WMS',
      version: '1.1.1',
      request: 'GetFeatureInfo',
      layers: layer,
      query_layers: layer,
      styles: '',
      srs: 'EPSG:4326',
      bbox: `${lon - d},${lat - d},${lon + d},${lat + d}`,
      width: '101',
      height: '101',
      x: '50',
      y: '50',
      info_format: 'application/json',
    });
    const res = await fetch(`${NATIONAL_ORTHO_WMS_URL}?${params}`, { signal });
    if (res.ok && orthoCovers(await res.json())) return year;
  }
  return null;
}

/** `2025-06-08` → `08.06.2025`; a bare year passes through. */
export function formatImageryDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : date;
}
